import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/store.inteface';
import { FilterDrawer } from 'pages/Franchisee/Reports/FilterDrawer';
import { getReportsAction } from 'store/modules/reports/actions';
import { Loading } from 'pages/Franchisee/TimelineActions/DocumentRequest/Loading';
import { Wrapper, Title } from './styles';
import { DownloadMultipleButton } from './downloadButton';
import { ShowMoreButton } from './ShowMoreButton';
import { NoContent } from './NoContent';
import { TableReports } from './components/Table';

export interface ReportWrapperProps {
  title: string;
}

export const ReportWrapper = ({ title }: ReportWrapperProps) => {
  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );
  const { reports, error } = useSelector(
    (state: ApplicationState) => state.reports,
  );

  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const dispatch = useDispatch();

  const franchiseCode = stores.map(({ code }) => code).slice(0, 100);

  function handleSelect(report, code) {
    setSelected(prevSelected => {
      const newSelection = { report, code };
      const isSelected = prevSelected.some(
        selection => selection.report === report && selection.code === code,
      );

      if (isSelected) {
        return prevSelected.filter(
          selection => selection.report !== report || selection.code !== code,
        );
      }
      return [...prevSelected, newSelection];
    });
  }

  const reportsSelected = selected.map(selection => selection.report);
  const franchises = selected.map(selection => selection.code);
  const franchiseSelected = [...new Set(franchises)];

  async function handleClearFilters() {
    getReports();
  }

  const handleApplyFilters = async filters => {
    const codeString = JSON.stringify(franchiseCode);
    fetchReports(
      {
        start: filters.start,
        end: filters.end,
      },
      filters.selectedFranchises.length > 0
        ? JSON.stringify(filters.selectedFranchises)
        : codeString,
    );
  };

  const fetchReports = useCallback(
    (filters, franchise) => {
      dispatch(
        getReportsAction(
          {
            start: filters ? filters.start : '',
            end: filters ? filters.end : '',
            limit: 8,
            showMore,
          },
          franchise,
        ),
      );
    },
    [dispatch, showMore],
  );

  const getReports = useCallback(() => {
    if (stores.length > 0) {
      const filters = {
        start: '',
        end: '',
      };
      const codeString = JSON.stringify(franchiseCode);
      fetchReports(filters, codeString);
    }
  }, [stores, fetchReports]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  useEffect(() => {
    setLoading(false);
    if (
      (reports.response.length === 0 && error === null) ||
      stores.length === 0
    ) {
      setLoading(true);
    }
  }, [reports, error, stores]);

  return (
    <Wrapper>
      <header>
        <Title>{title}</Title>
        <div
          className="header-content"
          style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <DownloadMultipleButton
            selected={reportsSelected}
            franshiceCode={franchiseSelected}
          />
          <FilterDrawer
            franchises={stores}
            onApplyFilter={handleApplyFilters}
            onClearFilter={handleClearFilters}
          />
        </div>
      </header>
      {!error && (
        <>
          <TableReports
            reports={reports}
            selected={selected}
            handleSelect={(id, pdvNumber) => handleSelect(id, pdvNumber)}
          />
          <footer>
            <ShowMoreButton
              setShowMore={setShowMore}
              hasMore={reports.hasMore}
              size={reports.response.length}
            />
          </footer>
        </>
      )}
      {loading && <Loading />}
      {error && <NoContent error={error} />}
    </Wrapper>
  );
};
